import { createContext, useContext, useState, useEffect } from "react";
import {
  collection, doc, setDoc, updateDoc, deleteDoc, getDoc, getDocs,
  onSnapshot, orderBy, query, serverTimestamp,
  runTransaction, writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePricing, isNightSlot } from "./PricingContext";

const BookingContext = createContext(null);

export const NEXT_DAY_SLOTS = new Set(["00:00", "01:00", "02:00", "03:00", "04:00"]);

// Number of bookable courts per sport
export const SPORT_COURT_COUNT = { "Oval Cricket": 1, "Box Cricket": 2, "Pickleball": 2 };

// Lock statuses that mean "this slot is free for new bookings"
const RELEASED_STATUSES = new Set(["cancelled", "failed", "released"]);

// Returns the lowest-numbered court (1-based) that has no conflict with the
// requested slots on the given date. Falls back to 1 if all are taken (the
// UI's slotStatus check should prevent this in normal flow).
function assignCourt(allBookings, date, sportName, regularSlots, nextDaySlots) {
  const total = SPORT_COURT_COUNT[sportName] || 1;
  if (total === 1) return 1;

  const active = allBookings.filter((b) =>
    b.date === date &&
    b.sport === sportName &&
    !["cancelled", "failed"].includes(b.status)
  );

  for (let court = 1; court <= total; court++) {
    const courtBookings = active.filter((b) => (b.courtNumber || 1) === court);
    const conflict = courtBookings.some((b) =>
      regularSlots.some((s) => (b.slots || []).includes(s)) ||
      nextDaySlots.some((s) => (b.nextDaySlots || []).includes(s))
    );
    if (!conflict) return court;
  }
  return 1;
}

// Returns Firestore doc refs for every slot lock that covers the given booking.
// Lock doc ID: "{date}_{sport}_court{court}_{slot}"
function slotLockRefs(date, sport, court, regularSlots, nextDaySlots) {
  return [...regularSlots, ...nextDaySlots].map((s) =>
    doc(db, "slot_locks", `${date}_${sport}_court${court}_${s}`)
  );
}

// Releases all slot locks for a booking by marking them "released".
// Uses set-merge so it works even if the lock doc was somehow deleted.
// Module-level so it can be called without React state closure issues.
async function releaseSlotLocks(booking, reason = "released") {
  const { date, sport, courtNumber = 1, slots = [], nextDaySlots = [] } = booking;
  const refs = slotLockRefs(date, sport, courtNumber, slots, nextDaySlots);
  if (!refs.length) return;
  try {
    const batch = writeBatch(db);
    refs.forEach((r) =>
      batch.set(r, { status: "released", releasedReason: reason, releasedAt: serverTimestamp() }, { merge: true })
    );
    await batch.commit();
    console.log(`[releaseSlotLocks] Released ${refs.length} lock(s) for booking ${booking.id} — reason: ${reason}`);
  } catch (e) {
    console.warn("[releaseSlotLocks] batch failed (non-fatal):", e.message);
  }
}

// Scans all slot_locks, identifies orphaned ones (parent booking is failed/cancelled/missing),
// and marks them "released". Safe to run multiple times (idempotent).
// Called once on admin dashboard mount to self-heal any existing orphans.
export async function cleanupOrphanedLocks() {
  console.log("[CleanupLocks] Starting orphaned lock scan...");
  let locksSnap;
  try {
    locksSnap = await getDocs(collection(db, "slot_locks"));
  } catch (e) {
    console.warn("[CleanupLocks] Could not read slot_locks:", e.message);
    return { released: 0, checked: 0 };
  }

  if (locksSnap.empty) {
    console.log("[CleanupLocks] slot_locks collection empty — nothing to check");
    return { released: 0, checked: 0 };
  }

  // Only check locks that are not already released
  const activeLocks = locksSnap.docs.filter((d) => !RELEASED_STATUSES.has(d.data().status || ""));
  console.log(`[CleanupLocks] ${locksSnap.size} total locks, ${activeLocks.length} active (non-released)`);

  if (!activeLocks.length) {
    console.log("[CleanupLocks] ✅ No active locks to check");
    return { released: 0, checked: 0 };
  }

  const batch = writeBatch(db);
  let released = 0;

  for (const lockDoc of activeLocks) {
    const lockData = lockDoc.data();
    const bookingId = lockData.bookingId;

    let parentStatus = "not_found";
    if (bookingId) {
      try {
        const bSnap = await getDoc(doc(db, "bookings", bookingId));
        parentStatus = bSnap.exists() ? bSnap.data().status : "not_found";
      } catch (e) {
        console.warn(`[CleanupLocks] Could not read parent booking ${bookingId}:`, e.message);
        continue;
      }
    }

    const parentIsDead = !bookingId || ["cancelled", "failed", "not_found"].includes(parentStatus);
    if (parentIsDead) {
      console.log(`[CleanupLocks] Releasing orphan ${lockDoc.id} — parent ${bookingId || "(none)"} is "${parentStatus}"`);
      batch.set(lockDoc.ref, {
        status: "released",
        releasedReason: "orphan_cleanup",
        releasedAt: serverTimestamp(),
      }, { merge: true });
      released++;
    }
  }

  if (released > 0) {
    await batch.commit();
    console.log(`[CleanupLocks] ✅ Released ${released} orphaned lock(s)`);
  } else {
    console.log("[CleanupLocks] ✅ All active locks have live parent bookings");
  }

  return { released, checked: activeLocks.length };
}

// Normalise Firestore Timestamp, plain object {seconds}, or ISO string → ms
function toMs(ts) {
  if (!ts) return null;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  return new Date(ts).getTime();
}

// Pricing fallback constants (used when Firestore pricing hasn't loaded yet)
export const WEEKDAY_RATE = 800;
export const WEEKEND_RATE = 1200;

export function isWeekend(dateStr) {
  const d = new Date(dateStr);
  return d.getDay() === 0 || d.getDay() === 6;
}

// Static fallback — still exported so PaymentSummary/BookingForm can import it
export function getRate(dateStr) {
  return isWeekend(dateStr) ? WEEKEND_RATE : WEEKDAY_RATE;
}

export function BookingProvider({ children }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  });
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookingForm, setBookingForm] = useState({ name: "", phone: "", team: "", players: "" });
  const [bookings, setBookings] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [user, setUser] = useState(null);
  const [partnerCode, setPartnerCode] = useState(null);
  const [blocks, setBlocks] = useState([]);

  const { getSportRate, getSlotPrice } = usePricing();

  // Admin state
  const [blockedDates, setBlockedDates] = useState([]);

  // Real-time bookings listener
  useEffect(() => {
    console.log("[onSnapshot] Setting up Firestore listener...");
    const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log("[onSnapshot] Received", snapshot.docs.length, "bookings from Firestore");
      const data = snapshot.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));
      setBookings(data);
    }, (error) => {
      console.error("[onSnapshot] Firestore listener ERROR:", error.code, error.message);
    });
    return () => unsubscribe();
  }, []);

  // Real-time blocks listener
  useEffect(() => {
    const q = query(collection(db, "blocks"), orderBy("date", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));
      setBlocks(data);
    }, (error) => {
      console.error("[blocks listener] ERROR:", error.code, error.message);
    });
    return () => unsubscribe();
  }, []);

  const toggleSlot = (slot) => {
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  const clearSlots = () => setSelectedSlots([]);

  // Raw total — no promo deduction. Components apply calcDiscount from PromoContext on top.
  const calcTotal = () => {
    const courtKey = selectedSport?.courtKey || "Oval Cricket Ground";
    return selectedSlots.reduce((sum, slot) => sum + getSlotPrice(courtKey, slot, selectedDate), 0);
  };

  // Step 1: atomically reserve slot locks + create pending booking.
  // The transaction does two read passes to be self-healing:
  //   Pass 1 — read all slot lock docs
  //   Pass 2 — for any active-looking lock, verify its parent booking is still live
  //            If parent is dead (failed/cancelled/missing) → auto-release the lock and proceed
  //            If parent is genuinely active → throw SLOT_TAKEN
  // All reads complete before any writes (Firestore transaction requirement).
  const createPendingBooking = async (id, finalTotal, extra = {}) => {
    const regularSlots  = [...selectedSlots].filter((s) => !NEXT_DAY_SLOTS.has(s)).sort();
    const nextDaySlots  = [...selectedSlots].filter((s) =>  NEXT_DAY_SLOTS.has(s)).sort();
    const courtKey   = selectedSport?.courtKey || "Oval Cricket Ground";
    const daySlots   = selectedSlots.filter((s) => !isNightSlot(s));
    const nightSlots = selectedSlots.filter((s) =>  isNightSlot(s));
    const dayTotal   = daySlots.reduce((sum, s) => sum + getSlotPrice(courtKey, s, selectedDate), 0);
    const nightTotal = nightSlots.reduce((sum, s) => sum + getSlotPrice(courtKey, s, selectedDate), 0);
    const sportName   = selectedSport?.name || "Oval Cricket";
    const courtNumber = assignCourt(bookings, selectedDate, sportName, regularSlots, nextDaySlots);

    const booking = {
      id,
      date: selectedDate,
      slots: regularSlots,
      nextDaySlots,
      courtNumber,
      daySlots,
      nightSlots,
      dayTotal,
      nightTotal,
      name: bookingForm.name,
      phone: bookingForm.phone,
      sport: sportName,
      total: finalTotal !== undefined ? finalTotal : calcTotal(),
      status: "pending",
      createdAt: serverTimestamp(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      platform: typeof navigator !== "undefined" ? navigator.platform : null,
      viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : null,
      referrer: typeof document !== "undefined" ? (document.referrer || null) : null,
      ...extra,
    };

    const lockRefs   = slotLockRefs(selectedDate, sportName, courtNumber, regularSlots, nextDaySlots);
    const bookingRef = doc(db, "bookings", id);

    console.log("[createPendingBooking] Starting atomic transaction — id:", id, "locks:", lockRefs.length);

    try {
      await runTransaction(db, async (tx) => {
        // ── Pass 1: read all lock docs ────────────────────────────────────────
        const lockSnaps = await Promise.all(lockRefs.map((r) => tx.get(r)));

        // Identify active-looking locks that need parent-booking verification
        const locksNeedingVerification = lockSnaps
          .filter((s) => s.exists() && !RELEASED_STATUSES.has(s.data().status || ""))
          .map((s) => ({ lockSnap: s, bookingId: s.data().bookingId }))
          .filter((x) => x.bookingId);

        // ── Pass 2: read parent bookings for all suspicious locks ─────────────
        const parentRefs  = locksNeedingVerification.map((x) => doc(db, "bookings", x.bookingId));
        const parentSnaps = parentRefs.length
          ? await Promise.all(parentRefs.map((r) => tx.get(r)))
          : [];

        const parentStatusByBookingId = new Map();
        locksNeedingVerification.forEach((x, i) => {
          const ps = parentSnaps[i];
          parentStatusByBookingId.set(x.bookingId, ps.exists() ? ps.data().status : "not_found");
        });

        // ── Evaluate each lock — collect orphans and check for real conflicts ──
        const orphanedLockIds = [];
        for (const snap of lockSnaps) {
          if (!snap.exists()) continue;

          const lockStatus = snap.data().status || "";
          if (RELEASED_STATUSES.has(lockStatus)) continue; // already released

          const parentBookingId = snap.data().bookingId;
          const parentStatus    = parentStatusByBookingId.get(parentBookingId) ?? "unknown";

          if (["cancelled", "failed", "not_found"].includes(parentStatus)) {
            // Parent booking is dead — orphaned lock, self-heal
            console.warn("[createPendingBooking] Auto-releasing orphaned lock:", snap.id, "parent:", parentBookingId, "status:", parentStatus);
            orphanedLockIds.push(snap.id);
            continue;
          }

          // Genuine active lock held by a live booking
          console.warn("[createPendingBooking] SLOT_TAKEN — lock:", snap.id, "parent:", parentBookingId, "status:", parentStatus);
          throw new Error("SLOT_TAKEN");
        }

        // ── All reads done — now do all writes ────────────────────────────────

        // Release any orphaned locks discovered above
        for (const lockId of orphanedLockIds) {
          tx.set(doc(db, "slot_locks", lockId), {
            status: "released",
            releasedReason: "auto_heal",
            releasedAt: serverTimestamp(),
          }, { merge: true });
        }

        // Create the new booking and claim its slot locks
        tx.set(bookingRef, booking);
        const allSlots = [...regularSlots, ...nextDaySlots];
        for (let i = 0; i < lockRefs.length; i++) {
          tx.set(lockRefs[i], {
            bookingId:  id,
            status:     "pending",
            date:       selectedDate,
            sport:      sportName,
            court:      courtNumber,
            slot:       allSlots[i],
            createdAt:  serverTimestamp(),
          });
        }
      });

      console.log("[createPendingBooking] ✅ Transaction succeeded for:", id);
    } catch (e) {
      if (e.message === "SLOT_TAKEN") throw e;
      console.error("[createPendingBooking] ❌ Transaction FAILED:", e.code, e.message, e);
      throw e;
    }

    return { ...booking, createdAt: new Date().toISOString() };
  };

  // Step 2: update status after payment outcome.
  // Reads the booking FRESH from Firestore (not from stale React state) before
  // releasing locks, so release always works even when called immediately after creation.
  const updateBookingStatus = async (id, status, extra = {}) => {
    console.log("[updateBookingStatus] Updating", id, "→", status);
    const payload = status === "failed"
      ? { status, errorTime: serverTimestamp(), ...extra }
      : { status, ...extra };
    try {
      await updateDoc(doc(db, "bookings", id), payload);
      console.log("[updateBookingStatus] updateDoc succeeded for:", id);
    } catch (e) {
      console.error("[updateBookingStatus] updateDoc FAILED:", e.code, e.message);
      throw e;
    }
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status, ...extra } : b))
    );
    // Release locks on failure — read fresh from Firestore, never trust React state here
    if (status === "failed") {
      try {
        const freshSnap = await getDoc(doc(db, "bookings", id));
        if (freshSnap.exists()) {
          await releaseSlotLocks(freshSnap.data(), "payment_failed");
        }
      } catch (e) {
        console.warn("[updateBookingStatus] Lock release failed (non-fatal):", e.message);
      }
    }
  };

  // Expire pending bookings older than 30 minutes.
  // Uses a Firestore transaction per booking so a webhook-confirmed booking
  // is never overwritten. Releases slot locks atomically inside the transaction.
  const expirePendingBookings = async () => {
    const cutoff = Date.now() - 30 * 60 * 1000;
    const stale = bookings.filter((b) => {
      if (b.status !== "pending") return false;
      if (b.webhookConfirmed) return false;
      const ms = toMs(b.createdAt);
      return ms !== null && ms < cutoff;
    });
    console.log(`[Expiry] Checking ${bookings.filter(b => b.status === "pending").length} pending booking(s) for expiry`);
    if (!stale.length) return;
    await Promise.all(
      stale.map(async (b) => {
        const ageMin = Math.round((Date.now() - toMs(b.createdAt)) / 60000);
        console.log(`[Expiry] Booking ${b.firestoreId} is ${ageMin} min old — attempting conditional expiry`);
        try {
          await runTransaction(db, async (tx) => {
            const ref  = doc(db, "bookings", b.firestoreId);
            const snap = await tx.get(ref);
            if (!snap.exists()) {
              console.log(`[Expiry] Skipping ${b.firestoreId} — document no longer exists`);
              return;
            }
            const { status: currentStatus, webhookConfirmed: alreadyWebhook } = snap.data();
            if (currentStatus !== "pending" || alreadyWebhook) {
              console.log(`[Expiry] Skipping ${b.firestoreId} — status="${currentStatus}" webhookConfirmed=${alreadyWebhook}`);
              return;
            }
            console.log(`[Expiry] Expiring ${b.firestoreId} (${ageMin} min old, still pending)`);
            tx.update(ref, { status: "failed", failureReason: "expired", errorTime: serverTimestamp() });
            // set-merge so it works even if a lock doc was somehow missing
            const lockRefs = slotLockRefs(b.date, b.sport, b.courtNumber || 1, b.slots || [], b.nextDaySlots || []);
            lockRefs.forEach((lr) =>
              tx.set(lr, { status: "released", releasedReason: "expired", releasedAt: serverTimestamp() }, { merge: true })
            );
          });
        } catch (e) {
          console.error(`[Expiry] Transaction failed for ${b.firestoreId}:`, e.message);
        }
      })
    );
  };

  // Step 3: clear form/slots after confirmed
  const finalizeBooking = () => {
    setSelectedSlots([]);
    setBookingForm({ name: "", phone: "", team: "", players: "" });
    setPartnerCode(null);
  };

  const createBlock = async (date, slots, nextDaySlots, reason, courts = ["all"]) => {
    const id = "BLK" + Date.now().toString().slice(-8);
    const block = { id, date, slots, nextDaySlots, reason, courts, createdAt: serverTimestamp() };
    await setDoc(doc(db, "blocks", id), block);
    return block;
  };

  const deleteBlock = async (firestoreId) => {
    await deleteDoc(doc(db, "blocks", firestoreId));
  };

  const cancelBooking = async (id, extra = {}) => {
    // Optimistic update
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status: "cancelled", ...extra } : b))
    );
    try {
      await updateDoc(doc(db, "bookings", id), { status: "cancelled", ...extra });
      // Read fresh from Firestore — don't rely on stale React state for slots/court
      const freshSnap = await getDoc(doc(db, "bookings", id));
      if (freshSnap.exists()) {
        await releaseSlotLocks(freshSnap.data(), "cancelled");
      }
    } catch (e) {
      console.error("Cancel error:", e);
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: "confirmed" } : b))
      );
    }
  };

  return (
    <BookingContext.Provider
      value={{
        selectedDate, setSelectedDate,
        selectedSlots, setSelectedSlots, toggleSlot, clearSlots,
        bookingForm, setBookingForm,
        bookings, setBookings,
        createPendingBooking, updateBookingStatus, finalizeBooking,
        cancelBooking, expirePendingBookings,
        calcTotal,
        selectedSport, setSelectedSport,
        user, setUser,
        partnerCode, setPartnerCode,
        blocks, createBlock, deleteBlock,
        blockedDates, setBlockedDates,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  return useContext(BookingContext);
}
